import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  Carousel,
  CarouselItem,
  Icon,
  OptimizedImage,
  Skeleton,
  SkeletonAvatar,
  SkeletonText,
  SocialShare,
  StarRating,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ToastProvider,
  Tooltip,
  VideoEmbed,
  showToast,
} from "@site-factory/ui";
import { SectionShell } from "@site-factory/ui";

export default function StyleGuidePage() {
  const [interactiveRating, setInteractiveRating] = useState(4);
  const sampleLogos = [
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=600&q=60",
    "https://images.unsplash.com/photo-1521790945508-bf2a36314e85?auto=format&fit=crop&w=600&q=60",
    "https://images.unsplash.com/photo-1489515217757-5fd1be406fef?auto=format&fit=crop&w=600&q=60",
    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=600&q=60",
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=60",
  ];

  return (
    <div className="space-y-12 py-12">
      <SectionShell>
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-neutral-500 uppercase tracking-wide">Style Guide</p>
            <h1 className="text-3xl font-bold">UI Components</h1>
          </div>
          <Button onClick={() => showToast({ title: "Hello!", description: "This is a toast example.", variant: "success" })}>
            Trigger Toast
          </Button>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Buttons</h2>
              <div className="flex flex-wrap gap-3">
                <Button>Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="soft">Soft</Button>
                <Button variant="ghost">Ghost</Button>
                <Button loading loadingText="Loading...">
                  Loading
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Tabs</h2>
              <Tabs defaultValue="one">
                <TabsList>
                  <TabsTrigger value="one">Tab One</TabsTrigger>
                  <TabsTrigger value="two">Tab Two</TabsTrigger>
                </TabsList>
                <TabsContent value="one">This is tab one content.</TabsContent>
                <TabsContent value="two">This is tab two content.</TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Accordion</h2>
              <Accordion type="single" collapsible>
                <AccordionItem value="a1">
                  <AccordionTrigger>Question 1</AccordionTrigger>
                  <AccordionContent>Answer for question 1.</AccordionContent>
                </AccordionItem>
                <AccordionItem value="a2">
                  <AccordionTrigger>Question 2</AccordionTrigger>
                  <AccordionContent>Answer for question 2.</AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Tooltips & Badges</h2>
              <div className="flex items-center gap-4">
                <Tooltip label="Tooltip example">
                  <Button variant="outline">
                    <Icon name="lightbulb" className="h-4 w-4 sf-icon-animate" />
                    Hover me
                  </Button>
                </Tooltip>
                <Badge>Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Skeletons</h2>
              <div className="flex items-center gap-3">
                <SkeletonAvatar size={48} />
                <SkeletonText lines={2} className="w-48" />
              </div>
              <Skeleton className="h-32 w-full rounded-lg" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Media</h2>
              <div className="grid gap-4">
                <div className="grid md:grid-cols-2 gap-3">
                  <OptimizedImage
                    src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=60"
                    alt="Ocean"
                    aspectRatio="16/9"
                    placeholder="blur"
                    blurDataURL="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=10&q=10"
                    className="rounded-lg"
                  />
                  <VideoEmbed provider="youtube" videoId="jNQXAC9IVRw" title="Sample video" />
                </div>
                <div className="rounded-xl overflow-hidden">
                  <video
                    className="w-full h-full object-cover aspect-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=60"
                  >
                    <source src="https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4" type="video/mp4" />
                  </video>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Carousel</h2>
              <div className="rounded-xl overflow-hidden">
                <Carousel autoplay interval={2500} showArrows showDots slidesToShow={{ mobile: 1, tablet: 2, desktop: 3 }}>
                  {sampleLogos.map((src, idx) => (
                    <CarouselItem key={idx} className="flex items-center justify-center p-0">
                      <img src={src} alt={`Sample ${idx + 1}`} className="h-44 w-full object-cover" />
                    </CarouselItem>
                  ))}
                </Carousel>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Ratings</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <StarRating value={4.5} showValue label="Customer satisfaction" />
                </div>
                <div className="flex items-center gap-3">
                  <StarRating value={3.2} showValue label="Feature completeness" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-neutral-600">Interactive example</p>
                  <StarRating
                    value={interactiveRating}
                    interactive
                    showValue
                    label="Tap to rate"
                    onChange={setInteractiveRating}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3">
              <h2 className="text-xl font-semibold">Animated Links</h2>
              <p className="text-sm text-neutral-600">
                Opt-in underline animation using <code>sf-link</code>.
              </p>
              <a href="#" className="demo-animated-link text-primary font-semibold inline-block">
                See the underline animate
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold">Social Share</h2>
              <p className="text-sm text-neutral-600">
                Inline buttons with Copy link fallback.
              </p>
              <SocialShare title="Check out this template" url="https://example.com" />
            </CardContent>
          </Card>
        </div>
      </SectionShell>
      <ToastProvider />
    </div>
  );
}
